/**
 * src/data/listFilters.js — shared smart-list filter definitions.
 *
 * Extracted from ListsPage.js so other surfaces (the People page's Lists
 * multi-select filter, future board/list views) can evaluate smart-list
 * predicates without duplicating the field schema or the matchesFilter
 * implementation.
 *
 * Pure data module — no React, no Supabase. Importing this anywhere is
 * cheap and side-effect-free.
 */

// ── Segment filter definitions ──
// Module-level skeleton — Tier B fields (status/source) carry empty options
// so consumers can overlay live override-aware options via useFieldOptions.
// matchesFilter only reads `def.type`, never `def.options`, so the placeholder
// is safe for the pure-function path.
export const SEG_FIELDS = [
  { id: "status", label: "Status", type: "select", options: [] },
  { id: "source", label: "Lead Source", type: "select", options: [] },
  { id: "score_min", label: "Score ≥", type: "number" },
  { id: "score_max", label: "Score ≤", type: "number" },
  { id: "company", label: "Company contains", type: "text" },
  { id: "title", label: "Job Title contains", type: "text" },
  { id: "industry", label: "Industry", type: "text" },
  { id: "city", label: "City", type: "text" },
  { id: "state", label: "State", type: "text" },
  { id: "tags", label: "Tag contains", type: "text" },
  { id: "has_phone", label: "Has Phone", type: "boolean" },
  { id: "has_email", label: "Has Email", type: "boolean" },
  { id: "leadStage", label: "Lead Stage", type: "select", options: ["new", "contacted", "qualified", "proposal", "won", "lost"] },
  { id: "created_after", label: "Created After", type: "date" },
  { id: "created_before", label: "Created Before", type: "date" },
  // ── Disposition-driven filters (commit D of dispositions feature) ──
  // The three boolean flags map to denormalized contact columns set by
  // applyDisposition. The "disposition" select takes a disposition_id (or
  // array of them); options are populated by the consuming UI from
  // store.callDispositions since this module is pure data and doesn't
  // import the store.
  { id: "do_not_call",      label: "Do Not Call",       type: "boolean" },
  { id: "qualified",        label: "Qualified",         type: "boolean" },
  { id: "awaiting_requeue", label: "Awaiting requeue",  type: "boolean" },
  { id: "disposition",      label: "Last disposition",  type: "select", options: [] },
];

export const SEG_MAP = Object.fromEntries(SEG_FIELDS.map(f => [f.id, f]));

export function matchesFilter(c, f) {
  const def = SEG_MAP[f.id];
  const v = String(f.value || "").toLowerCase();
  if (!v && def?.type !== "boolean") return true;
  switch (f.id) {
    case "status": return (c.status || "").toLowerCase() === v;
    case "source": return (c.source || "").toLowerCase() === v;
    case "score_min": return (c.score || 0) >= Number(f.value);
    case "score_max": return (c.score || 0) <= Number(f.value);
    case "company": return (c.company || c.co || "").toLowerCase().includes(v);
    case "title": return (c.title || "").toLowerCase().includes(v);
    case "industry": return (c.industry || "").toLowerCase().includes(v);
    case "city": return (c.city || "").toLowerCase().includes(v);
    case "state": return (c.state || "").toLowerCase().includes(v);
    case "tags": return (c.tags || []).some(t => t.toLowerCase().includes(v));
    case "has_phone": return f.value === "true" ? !!(c.ph || c.phone) : !(c.ph || c.phone);
    case "has_email": return f.value === "true" ? !!c.email : !c.email;
    case "leadStage": return (c.leadStage || "").toLowerCase() === v;
    case "created_after": return (c.created || "") >= f.value;
    case "created_before": return (c.created || "") <= f.value;
    // Disposition flag filters. f.value === "true" means "match contacts
    // with the flag set"; "false" means "match contacts without it".
    case "do_not_call": return f.value === "true" ? !!c.do_not_call : !c.do_not_call;
    case "qualified":   return f.value === "true" ? !!c.qualified_at : !c.qualified_at;
    case "awaiting_requeue": {
      const isAwaiting = !!c.requeue_after && new Date(c.requeue_after).getTime() > Date.now();
      return f.value === "true" ? isAwaiting : !isAwaiting;
    }
    // Last-disposition match. f.value can be a single disposition id or
    // an array of ids (multi-select). Empty string falls through via the
    // top-of-function `if (!v && def?.type !== "boolean") return true`.
    case "disposition": {
      if (Array.isArray(f.value)) return f.value.length === 0 || f.value.includes(c.last_disposition_id);
      return c.last_disposition_id === f.value;
    }
    default: {
      // Custom field filter — check contact properties
      if (f.id.startsWith("cf__")) {
        const parts = f.id.split("__");
        const fieldId = parts[2]; // cf__entity__fieldId
        const actual = String(c[fieldId] ?? c.properties?.[fieldId] ?? "").toLowerCase();
        if (!v) return true;
        return actual.includes(v) || actual === v;
      }
      return true;
    }
  }
}
